import React, { useContext, useState, useEffect } from 'react';
import Divider from '@mui/material/Divider';
import { Grid, Typography, Stack, IconButton } from '@mui/material';
import {
    MetLabel,
    PrimaryButton,
    SecondaryButton,
    MidScreenLoader,
    MetWidgetPaper,
    MetDescription,
} from 'components/shared/common';
import { useForm, FormProvider, SubmitHandler } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useAppDispatch } from 'hooks';
import ControlledTextField from 'components/shared/common/ControlledInputComponents/ControlledTextField';
import { openNotification } from 'services/notificationService/notificationSlice';
import { MapContext } from './MapContext';
import { postMap, previewShapeFile } from 'services/widgetService/MapService';
import { WidgetDrawerContext } from '../WidgetDrawerContext';
import ShapeFileUpload from 'components/admin/engagement/form/EngagementWidgets/Map/ShapeFileUpload/FileUpload';
import { geoJSONDecode } from './utils';
import { GeoJSON } from 'geojson';
import LinkIcon from '@mui/icons-material/Link';
import { When } from 'react-if';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import * as turf from '@turf/turf';
import type { FeatureCollection, Point } from 'geojson';
import { WidgetTitle } from '../WidgetTitle';

const INVALID_SPATIAL_FILE_MESSAGE =
    'Unable to process this spatial file. Please upload a valid shapefile zip and try again.';

const getServerErrorMessage = (error: unknown, fallback: string) => {
    if (error && typeof error === 'object') {
        const errorResponseData = (error as { response?: { data?: unknown } }).response?.data;

        if (errorResponseData && typeof errorResponseData === 'object') {
            const responseData = errorResponseData as {
                message?: string;
            };

            if (typeof responseData.message === 'string' && responseData.message.trim().length > 0) {
                return responseData.message;
            }
        }
    }
    return fallback;
};

const schema = yup
    .object({
        markerLabel: yup.string().max(30, 'Markel label cannot exceed 30 characters'),
        shapefile: yup.mixed(),
        geojson: yup.mixed(),
        latitude: yup
            .number()
            .typeError('Invalid value for Latitude')
            .required('Latitude is required')
            .min(-90, 'Latitude must be greater than or equal to -90')
            .max(90, 'Latitude must be less than or equal to 90'),
        longitude: yup
            .number()
            .typeError('Invalid value for Longitude')
            .required('Longitude is required')
            .min(-180, 'Longitude must be greater than or equal to -180')
            .max(180, 'Longitude must be less than or equal to 180'),
        filename: yup.string().nullable(),
    })
    .required();

type DetailsForm = yup.TypeOf<typeof schema>;

const MapForm = () => {
    const dispatch = useAppDispatch();
    const { widget, mapData, isLoadingMap, setPreviewMapOpen, setPreviewMap, updateZoom } = useContext(MapContext);
    const { handleWidgetDrawerOpen } = useContext(WidgetDrawerContext);
    const [isCreating, setIsCreating] = useState(false);
    const [uploadName, setUploadName] = useState('');
    const [calculatingZoom, setCalculatingZoom] = useState(false);

    const methods = useForm<DetailsForm>({
        resolver: yupResolver(schema),
    });

    useEffect(() => {
        if (mapData) {
            methods.setValue('markerLabel', mapData?.marker_label || '');
            methods.setValue('latitude', mapData?.latitude || undefined);
            methods.setValue('longitude', mapData?.longitude || undefined);
            methods.setValue('geojson', mapData ? geoJSONDecode(mapData?.geojson) : undefined);
            methods.setValue('filename', mapData ? mapData.file_name : undefined);
        }
    }, [mapData]);

    const { handleSubmit, reset, trigger, watch } = methods;

    const [longitude, latitude, markerLabel, geojson, shapefile, filename] = watch([
        'longitude',
        'latitude',
        'markerLabel',
        'geojson',
        'shapefile',
        'filename',
    ]);

    const validateAndLoadShapeFile = async (file: File) => {
        const previewGeoJson = (await previewShapeFile({
            file,
        })) as unknown as FeatureCollection<Point>;

        updateZoom(previewGeoJson);
        const centerPoint = turf.center(previewGeoJson);
        methods.setValue('longitude', centerPoint.geometry.coordinates[0]);
        methods.setValue('latitude', centerPoint.geometry.coordinates[1]);

        return previewGeoJson;
    };

    const createMap = async (data: DetailsForm) => {
        if (!widget) {
            return;
        }

        const validatedData = await schema.validate(data);
        const { latitude, longitude, markerLabel, shapefile } = validatedData;

        if (shapefile) {
            try {
                await previewShapeFile({ file: shapefile });
            } catch (error) {
                throw new Error(getServerErrorMessage(error, INVALID_SPATIAL_FILE_MESSAGE));
            }
        }

        await postMap(widget.id, {
            widget_id: widget.id,
            engagement_id: widget.engagement_id,
            marker_label: markerLabel,
            longitude: longitude,
            latitude: latitude,
            file: shapefile,
        });
        dispatch(openNotification({ severity: 'success', text: 'A new map was successfully added' }));
    };

    const onSubmit: SubmitHandler<DetailsForm> = async (data: DetailsForm) => {
        if (!widget) {
            return;
        }
        try {
            setIsCreating(true);
            await createMap(data);
            setIsCreating(false);
            reset({});
            handleWidgetDrawerOpen(false);
        } catch (error) {
            dispatch(
                openNotification({
                    severity: 'error',
                    text: getServerErrorMessage(error, INVALID_SPATIAL_FILE_MESSAGE),
                }),
            );
            setIsCreating(false);
        }
    };

    const handlePreviewMap = async () => {
        const valid = await trigger(['latitude', 'longitude', 'markerLabel', 'shapefile']);
        const validatedData = await schema.validate({ latitude, longitude, markerLabel, geojson, shapefile });
        let previewGeoJson: turf.AllGeoJSON | GeoJSON | undefined;
        if (!valid) {
            return;
        }
        if (shapefile) {
            try {
                previewGeoJson = await validateAndLoadShapeFile(shapefile);
            } catch (error) {
                dispatch(
                    openNotification({
                        severity: 'error',
                        text: getServerErrorMessage(error, INVALID_SPATIAL_FILE_MESSAGE),
                    }),
                );
                return;
            }
        }
        setPreviewMap({
            longitude: validatedData.longitude,
            latitude: validatedData.latitude,
            markerLabel: validatedData.markerLabel,
            geojson: previewGeoJson ? previewGeoJson : validatedData.geojson,
        });
        setPreviewMapOpen(true);
    };

    const handleAddFile = async (files: File[]) => {
        if (files.length > 0) {
            setCalculatingZoom(true);
            try {
                await validateAndLoadShapeFile(files[0]);
                methods.setValue('shapefile', files[0]);
                methods.setValue('filename', undefined);
                return true;
            } catch (error) {
                methods.setValue('shapefile', undefined);
                dispatch(
                    openNotification({
                        severity: 'error',
                        text: getServerErrorMessage(error, INVALID_SPATIAL_FILE_MESSAGE),
                    }),
                );
                return false;
            } finally {
                setCalculatingZoom(false);
            }
        }

        methods.setValue('shapefile', undefined);
        setUploadName('');
        return true;
    };

    if (isLoadingMap) {
        return (
            <Grid container direction="row" alignItems={'flex-start'} justifyContent="flex-start" spacing={2}>
                <Grid item xs={12}>
                    <MidScreenLoader />
                </Grid>
            </Grid>
        );
    }

    if (!widget) {
        return null;
    }

    return (
        <Grid item xs={12} container alignItems="flex-start" justifyContent={'flex-start'} spacing={3}>
            <Grid item xs={12}>
                <WidgetTitle widget={widget} />
                <Divider sx={{ marginTop: '0.5em' }} />
            </Grid>
            <Grid item xs={12}>
                <FormProvider {...methods}>
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <Grid container direction="row" alignItems="baseline" justifyContent="flex-start" spacing={2}>
                            <Grid item xs={12}>
                                <ShapeFileUpload
                                    data-testid="shapefile-upload"
                                    handleAddFile={handleAddFile}
                                    savedFileName={uploadName}
                                    savedFile={methods.getValues('shapefile')}
                                />
                            </Grid>
                            <When condition={Boolean(filename)}>
                                <Grid item xs={12}>
                                    <MetLabel sx={{ marginBottom: '2px' }}>File Uploaded </MetLabel>
                                    <MetWidgetPaper elevation={1} sx={{ width: '100%' }}>
                                        <Grid
                                            container
                                            direction="row"
                                            alignItems={'center'}
                                            justifyContent="flex-start"
                                        >
                                            <Grid item xs>
                                                <Stack spacing={2} direction="row" alignItems="center">
                                                    <LinkIcon color="info" />
                                                    <Typography>{mapData?.file_name}</Typography>
                                                </Stack>
                                            </Grid>
                                            <IconButton
                                                onClick={() => {
                                                    methods.setValue('filename', undefined);
                                                    methods.setValue('geojson', undefined);
                                                }}
                                                sx={{ padding: 0, margin: 0 }}
                                                color="inherit"
                                                aria-label="Remove GeoJSON"
                                            >
                                                <HighlightOffIcon />
                                            </IconButton>
                                        </Grid>
                                    </MetWidgetPaper>
                                </Grid>
                            </When>
                            <Grid item xs={12}>
                                <MetLabel sx={{ marginBottom: '2px' }}>Latitude</MetLabel>
                                <MetDescription>Latitude in British Columbia is between 48.30 and 60.00</MetDescription>
                                <ControlledTextField
                                    name="latitude"
                                    variant="outlined"
                                    label=" "
                                    InputLabelProps={{
                                        shrink: false,
                                    }}
                                    fullWidth
                                    size="small"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <MetLabel sx={{ marginBottom: '2px' }}>Longitude</MetLabel>
                                <MetDescription>
                                    Longitude in British Columbia is between  -139.06 and -114.03
                                </MetDescription>
                                <ControlledTextField
                                    name="longitude"
                                    variant="outlined"
                                    label=" "
                                    InputLabelProps={{
                                        shrink: false,
                                    }}
                                    fullWidth
                                    size="small"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <MetLabel sx={{ marginBottom: '2px' }}>Marker Label</MetLabel>
                                <MetDescription>This text will appear next to your marker on the map</MetDescription>
                                <ControlledTextField
                                    name="markerLabel"
                                    variant="outlined"
                                    label=" "
                                    InputLabelProps={{
                                        shrink: false,
                                    }}
                                    fullWidth
                                    size="small"
                                />
                            </Grid>
                            <Grid item xs={12} container direction="row" justifyContent={'flex-end'}>
                                <Grid item>
                                    <SecondaryButton loading={calculatingZoom} onClick={handlePreviewMap}>
                                        Preview Map
                                    </SecondaryButton>
                                </Grid>
                            </Grid>
                            <Grid
                                item
                                xs={12}
                                container
                                direction="row"
                                spacing={1}
                                justifyContent={'flex-start'}
                                marginTop="2em"
                            >
                                <Grid item>
                                    <PrimaryButton type="submit" loading={isCreating}>{`Save & Close`}</PrimaryButton>
                                </Grid>
                                <Grid item>
                                    <SecondaryButton onClick={() => handleWidgetDrawerOpen(false)}>
                                        Cancel
                                    </SecondaryButton>
                                </Grid>
                            </Grid>
                        </Grid>
                    </form>
                </FormProvider>
            </Grid>
        </Grid>
    );
};

export default MapForm;
